import { AiInsight, AiInsightContent, AiInsightType } from '../../../services/ai-insight.service';
import { DEMO_APPS } from './apps.fixtures';

/**
 * AI insight fixtures.
 *
 * Each connected app has a full set of five insight types written against that app's own numbers
 * (see app-profiles.fixtures.ts), so the text on the dashboard, performance and sentiment cards
 * matches the charts and reviews on the same screens.
 */

type InsightSet = Record<AiInsightType, AiInsightContent>;

const hoursAgoIso = (hours: number) => new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();

// ------------------------------------------------------------------ Acme Shopping (8001)

const SHOPPING: InsightSet = {
  ANALYTICS_SUMMARY: {
    headline: 'Active users up 8.4% to 64,200, with the Autumn Sale driving new installs',
    summary:
      'Monthly active users grew 8.4% and sessions rose 9.6% to 231,000. New users climbed 12.1%, with a clear spike during the Autumn Sale when daily installs ran roughly 45 to 50% above normal. Engagement dipped briefly in the middle of the month when the checkout crash hit, then recovered once the hotfix was adopted. Weekends remain the strongest days, led by Sunday.',
    key_findings: [
      { finding: 'New users up 12.1% (11,850), peaking at about 1.5x daily average during the Autumn Sale', metric: 'newUsers', direction: 'up' },
      { finding: 'Sessions dipped about 7% on the two worst crash days before recovering', metric: 'sessions', direction: 'down' },
      { finding: 'Sunday and Saturday sessions run about 14 to 15% above the weekly average', metric: 'sessions', direction: 'up' },
      { finding: '30-day retention is 47.3%, with the United States (41%) and United Kingdom (14%) the largest markets', metric: 'retentionRate', direction: 'flat' },
    ],
    action_items: [
      { action: 'Re-run the Autumn Sale mechanics (auto-applied coupon) for the next campaign, since it converted new installs well', priority: 'high' },
      { action: 'Schedule promotional push notifications for Saturday and Sunday mornings when engagement peaks', priority: 'medium' },
      { action: 'Add an onboarding nudge for installs from campaigns to convert them into repeat sessions', priority: 'medium' },
    ],
    data_quality: { sufficient: true },
  },
  CRASH_SUMMARY: {
    headline: 'Checkout crash from v4.12.0 is resolved; crash-free sessions back above 99%',
    summary:
      'Crash-free sessions fell to 97.1% at the peak two days after v4.12.0 rolled out to everyone, caused by a null payment-sheet reference on Android 11 and 12. Hotfix 4.12.1 shipped nine days ago and crash rate returned to its 0.6% baseline within three days. The month closes at 99.1% crash-free, compared with about 99.5% last month.',
    key_findings: [
      { finding: 'Crash rate peaked at 2.9% (baseline 0.6%), all on Android 11/12 at the checkout payment step', metric: 'crashRate', direction: 'up' },
      { finding: 'Hotfix 4.12.1 restored crash-free sessions above 99% within three days of release', metric: 'crashFreeSessions', direction: 'up' },
      { finding: 'iOS was unaffected throughout the incident', metric: 'crashes', direction: 'flat' },
    ],
    action_items: [
      { action: 'Add a crash-free-session gate (99.5%) that must pass before a staged rollout goes beyond 25%', priority: 'high' },
      { action: 'Add Android 11/12 payment-flow devices to the pre-release test matrix', priority: 'high' },
      { action: 'Switch checkout-tagged crash alerts from a daily digest to real time', priority: 'medium' },
    ],
    data_quality: { sufficient: true },
  },
  PERFORMANCE_SUMMARY: {
    headline: 'Payment gateway latency is the one performance issue, pushing API response time to 486ms',
    summary:
      'Page response time (312ms) and network latency (88ms) are comfortably within target, and uptime is 99.82%. API response time is elevated at 486ms, driven almost entirely by slow responses from the EU payment gateway on the checkout confirmation call. One major incident remains open with the gateway provider.',
    key_findings: [
      { finding: 'API response time 486ms, versus 250ms target, concentrated in POST /checkout/confirm', metric: 'apiResponseTime', direction: 'up' },
      { finding: 'Page response time 312ms, well under the 500ms target', metric: 'responseTime', direction: 'flat' },
      { finding: 'Server uptime 99.82% with one open major incident', metric: 'serverUptime', direction: 'down' },
    ],
    action_items: [
      { action: 'Enable the EU fallback gateway route for checkout confirmation until the incident closes', priority: 'high' },
      { action: 'Make order confirmation asynchronous so slow gateway responses do not block the checkout screen', priority: 'medium' },
      { action: 'Review the payment provider SLA and credits for this month', priority: 'low' },
    ],
    data_quality: { sufficient: true },
  },
  REVIEW_SENTIMENT: {
    headline: 'Sentiment is 89% positive; a two-week checkout-crash cluster dominates recent negatives',
    summary:
      'Across 12,480 lifetime reviews, 89% are positive and the average rating is 4.5. In the recent feed, nearly every 1- and 2-star review falls in the window one to two weeks ago and mentions the checkout crash on Android. Since the hotfix, reviews turn positive again and several customers explicitly thank the team for the fast fix. Smaller recurring themes are notification delays and a request for dark mode.',
    key_findings: [
      { finding: 'Most negative reviews in the last 30 days cite crashes at the payment step on Android', metric: 'sentiment', direction: 'down' },
      { finding: 'Post-hotfix reviews praise the speed of the fix and the reliability of checkout', metric: 'sentiment', direction: 'up' },
      { finding: 'Apple Pay checkout, wishlist price alerts and easy returns are the most-praised features', metric: 'sentiment', direction: 'up' },
      { finding: 'Dark mode and notification timing are the top feature requests', metric: 'sentiment', direction: 'flat' },
    ],
    action_items: [
      { action: 'Reply to the 1- and 2-star checkout reviews acknowledging 4.12.1, and invite customers to update', priority: 'high' },
      { action: 'Follow up with the customer who reported a double charge and confirm the refund', priority: 'high' },
      { action: 'Add dark mode to the roadmap; it is the most repeated feature request', priority: 'medium' },
    ],
    data_quality: { sufficient: true },
  },
  EXECUTIVE_SUMMARY: {
    headline: 'Strong growth month, dented by one checkout incident that is now resolved',
    summary:
      'Acme Shopping grew users 8.4% and sessions 9.6%, helped by a successful Autumn Sale. The v4.12.0 checkout crash cost roughly two days of depressed sessions and produced a burst of negative reviews, but was fixed within five days of detection. The remaining risk is the EU payment gateway slowdown, which is still affecting checkout speed.',
    key_findings: [
      { finding: 'Users and sessions grew 8.4% and 9.6% with the sale driving new installs', metric: 'activeUsers', direction: 'up' },
      { finding: 'Checkout crash incident resolved; crash-free sessions back above 99%', metric: 'crashFreeSessions', direction: 'up' },
      { finding: 'EU payment gateway latency is an open risk to conversion', metric: 'apiResponseTime', direction: 'down' },
    ],
    action_items: [
      { action: 'Institute a release gate on crash-free sessions before rollouts pass 25%', priority: 'high' },
      { action: 'Close out the payment gateway incident and add failover capacity', priority: 'high' },
      { action: 'Plan the next campaign using the Autumn Sale mechanics', priority: 'medium' },
    ],
    data_quality: { sufficient: true },
  },
};

// ------------------------------------------------------------------ Acme Loyalty (8002)

const LOYALTY: InsightSet = {
  ANALYTICS_SUMMARY: {
    headline: 'Steady growth and the strongest retention in the portfolio at 58.6%',
    summary:
      'Monthly active users rose 3.1% to 28,900 and sessions grew 4.2% to 118,400. The double-points weekend produced the month\'s biggest spike, lifting sessions by roughly 16 to 20% on those days. Average session length is slightly shorter (3m 04s, down 0.9%), which is healthy for a quick, at-the-till app.',
    key_findings: [
      { finding: 'Double-points weekend lifted daily sessions 16 to 20%', metric: 'sessions', direction: 'up' },
      { finding: '30-day retention of 58.6%, the highest of any Acme app', metric: 'retentionRate', direction: 'up' },
      { finding: 'Average session duration 3m 04s, down 0.9%, consistent with faster redemptions', metric: 'sessionDuration', direction: 'flat' },
      { finding: 'The United States (52%) and Canada (17%) account for over two thirds of users', metric: 'activeUsers', direction: 'flat' },
    ],
    action_items: [
      { action: 'Schedule another points-multiplier event; the last one produced the largest engagement lift of the month', priority: 'high' },
      { action: 'Pilot household accounts to capture the partner and family sharing requested in reviews', priority: 'medium' },
      { action: 'Use the high retention to cross-promote the Acme Rewards beta to loyal members', priority: 'low' },
    ],
    data_quality: { sufficient: true },
  },
  CRASH_SUMMARY: {
    headline: 'Crash-free sessions at 99.6%, the best in the portfolio',
    summary:
      'Stability is excellent. Crash-free sessions are 99.6%, ANR rate is 0.15%, and no release this period introduced a regression. The few crashes recorded are isolated to the QR scanner on low-end devices.',
    key_findings: [
      { finding: 'Crash-free sessions 99.6% with no spikes across the period', metric: 'crashFreeSessions', direction: 'flat' },
      { finding: 'ANR rate 0.15%, well within Play Console thresholds', metric: 'anrRate', direction: 'flat' },
      { finding: 'Residual crashes cluster in the QR card scanner on devices with under 3 GB of RAM', metric: 'crashes', direction: 'flat' },
    ],
    action_items: [
      { action: 'Optimize the QR scanner camera pipeline for low-memory devices', priority: 'low' },
    ],
    data_quality: { sufficient: true },
  },
  PERFORMANCE_SUMMARY: {
    headline: 'Excellent performance across the board',
    summary:
      'All performance indicators are in the excellent range: page response 198ms, API response 142ms, network latency 71ms and 99.98% uptime. Two minor incidents this period were resolved within an hour each.',
    key_findings: [
      { finding: 'API response time 142ms, the fastest in the portfolio', metric: 'apiResponseTime', direction: 'flat' },
      { finding: 'Uptime 99.98% with two minor, resolved incidents', metric: 'serverUptime', direction: 'flat' },
    ],
    action_items: [
      { action: 'Use this app\'s backend configuration as the baseline when tuning Acme Shopping and Delivery Partner', priority: 'low' },
    ],
    data_quality: { sufficient: true },
  },
  REVIEW_SENTIMENT: {
    headline: 'Members love it: 94% positive, with delayed points the main complaint',
    summary:
      'Acme Loyalty holds a 4.6 average across 6,554 reviews, and 94% are positive. Reviewers praise the instant till scan, relevant offers and the tier rewards. The recurring negatives are points from online orders taking several days to appear, offer notifications arriving after the offer expires, and a home-screen widget that shows a stale balance.',
    key_findings: [
      { finding: '94% of lifetime reviews are positive; only 2.8% are negative', metric: 'sentiment', direction: 'up' },
      { finding: 'Delayed points crediting for online orders is the most common complaint', metric: 'sentiment', direction: 'down' },
      { finding: 'Targeted offers and birthday rewards are repeatedly called out as delightful', metric: 'sentiment', direction: 'up' },
    ],
    action_items: [
      { action: 'Reduce the online-order points credit delay, then announce it in a release note', priority: 'high' },
      { action: 'Fix the home-screen widget so it refreshes the balance in the background', priority: 'medium' },
      { action: 'Send offer notifications at least 24 hours before expiry', priority: 'medium' },
    ],
    data_quality: { sufficient: true },
  },
  EXECUTIVE_SUMMARY: {
    headline: 'A stable, well-loved app with room to cut the points-credit delay',
    summary:
      'Acme Loyalty is the portfolio\'s steadiest performer: high retention, excellent stability, 4.6-star ratings and modest growth. The clearest opportunity is operational rather than technical, namely speeding up points crediting for online orders, which drives most of the remaining negative feedback.',
    key_findings: [
      { finding: 'Retention of 58.6% and crash-free sessions of 99.6% lead the portfolio', metric: 'retentionRate', direction: 'up' },
      { finding: 'Points-credit delay is the main driver of negative sentiment', metric: 'sentiment', direction: 'down' },
    ],
    action_items: [
      { action: 'Resolve the online-order points delay with the commerce team', priority: 'high' },
      { action: 'Run a points-multiplier campaign quarterly', priority: 'medium' },
    ],
    data_quality: { sufficient: true },
  },
};

// ------------------------------------------------------------------ Acme Delivery Partner (8003)

const DELIVERY: InsightSet = {
  ANALYTICS_SUMMARY: {
    headline: 'Courier base up 24.6% and sessions up 27.3%, with 71% retention',
    summary:
      'Delivery Partner is the fastest-growing app in the portfolio. Monthly active couriers reached 9,450 (up 24.6%), new sign-ups rose 31%, and sessions grew 27.3% to 142,000. Couriers spend an average of 7m 41s per session, up 5.8%, and 71.2% are still active after 30 days. Friday is the peak day of the week, followed by Saturday.',
    key_findings: [
      { finding: 'Active couriers up 24.6% to 9,450, following the metro expansion', metric: 'activeUsers', direction: 'up' },
      { finding: 'New couriers up 31% (1,680) with strong 30-day retention of 71.2%', metric: 'newUsers', direction: 'up' },
      { finding: 'Friday sessions run about 22% above the weekly average', metric: 'sessions', direction: 'up' },
      { finding: 'Session length up 5.8% to 7m 41s as couriers batch more orders per shift', metric: 'sessionDuration', direction: 'up' },
    ],
    action_items: [
      { action: 'Staff support and dispatch for the Friday dinner peak, which is growing fastest', priority: 'high' },
      { action: 'Extend onboarding content for new couriers, since sign-ups are the main growth driver', priority: 'medium' },
      { action: 'Forecast load for the holiday season when courier volume typically doubles', priority: 'medium' },
    ],
    data_quality: { sufficient: true },
  },
  CRASH_SUMMARY: {
    headline: 'Crash-free sessions slipped to 99.1% as growth exposes memory pressure on older iPhones',
    summary:
      'Crash-free sessions are 99.1%, down from about 99.5% last month. The decline is concentrated on iPhone 11 and older running iOS 17, where the live route screen exhausts memory during long shifts and the OS terminates the app. ANR-like stalls also peaked at 1.1% during a recent dinner rush.',
    key_findings: [
      { finding: 'Crash-free sessions fell from about 99.5% to 99.1% over the month', metric: 'crashFreeSessions', direction: 'down' },
      { finding: 'Most terminations are memory-pressure kills on LiveRouteViewController on iPhone 11 and older', metric: 'crashes', direction: 'up' },
      { finding: 'Unresponsive-page stalls peaked at 1.1% during a recent dinner rush', metric: 'anrRate', direction: 'up' },
    ],
    action_items: [
      { action: 'Ship release 2.8.3, which caps the map tile cache at 120 MB', priority: 'high' },
      { action: 'Move order-list re-sorting off the main thread and throttle it to every 5 seconds', priority: 'high' },
      { action: 'Add a memory-pressure breadcrumb to crash reports for faster diagnosis', priority: 'medium' },
    ],
    data_quality: { sufficient: true },
  },
  PERFORMANCE_SUMMARY: {
    headline: 'Response time and network latency are nearing their limits as load grows',
    summary:
      'Page response time is 455ms against a 500ms target and network latency is 134ms, both in warning territory. Uptime is healthy at 99.91% and API response time is 268ms. Seven pages are flagged unresponsive, mostly the order-assignment list during busy periods. Six minor incidents were recorded, none major.',
    key_findings: [
      { finding: 'Page response time 455ms, 91% of the 500ms target', metric: 'responseTime', direction: 'up' },
      { finding: 'Network latency 134ms, the highest of the active apps', metric: 'networkLatency', direction: 'up' },
      { finding: 'Order-assignment screen accounts for five of the seven unresponsive pages', metric: 'unresponsivePages', direction: 'up' },
      { finding: 'Uptime 99.91% with six minor, resolved incidents', metric: 'serverUptime', direction: 'flat' },
    ],
    action_items: [
      { action: 'Prioritize the order-assignment performance work before the holiday peak', priority: 'high' },
      { action: 'Review regional routing for couriers in the newly launched metro areas', priority: 'medium' },
      { action: 'Set a performance budget for the courier app tied to onboarding targets', priority: 'low' },
    ],
    data_quality: { sufficient: true },
  },
  REVIEW_SENTIMENT: {
    headline: 'Couriers like the earnings tools, but freezes during rush hour are costing them orders',
    summary:
      'Acme Delivery Partner is rated 4.2 across 2,982 reviews, and 79% are positive. Couriers praise the earnings tracker, quick payouts and the heat map. Negative reviews concentrate on the app freezing during busy periods, battery drain from constant location tracking, GPS drift, and late order notifications when the phone is locked. Reviewers connect these issues directly to lost income.',
    key_findings: [
      { finding: 'Earnings tracker, instant payouts and the order heat map are the most-praised features', metric: 'sentiment', direction: 'up' },
      { finding: 'Freezes during the dinner rush are the top complaint and tied to missed orders', metric: 'sentiment', direction: 'down' },
      { finding: 'Battery drain on iPhone 11 and older appears in several recent reviews', metric: 'sentiment', direction: 'down' },
    ],
    action_items: [
      { action: 'Respond to the 1- and 2-star reviews about freezing and crashes with the planned fix timeline', priority: 'high' },
      { action: 'Show the tip amount before order acceptance, a request that appears across multiple reviews', priority: 'medium' },
      { action: 'Investigate delayed push notifications when the app is backgrounded', priority: 'medium' },
    ],
    data_quality: { sufficient: true },
  },
  EXECUTIVE_SUMMARY: {
    headline: 'Growing fast, now at the point where performance needs investment',
    summary:
      'Delivery Partner is the portfolio\'s growth story: couriers up 24.6% with excellent 71% retention. The same growth is now straining the app. Crash-free sessions, response time and latency are all drifting toward their limits, and reviews tie freezes directly to couriers\' earnings. Fixing the order-assignment screen and memory use ahead of the holiday peak is the most valuable near-term investment.',
    key_findings: [
      { finding: 'Couriers and sessions up 24.6% and 27.3% following the metro expansion', metric: 'activeUsers', direction: 'up' },
      { finding: 'Stability and responsiveness are degrading under load', metric: 'crashFreeSessions', direction: 'down' },
      { finding: 'Reviews link freezes to lost orders, a retention risk for couriers', metric: 'sentiment', direction: 'down' },
    ],
    action_items: [
      { action: 'Fund a performance sprint on order assignment and live route before the holiday peak', priority: 'high' },
      { action: 'Communicate the fix timeline to couriers in-app and through support', priority: 'medium' },
    ],
    data_quality: { sufficient: true },
  },
};

// ------------------------------------------------------------------ Acme Rewards Beta (8004)

const BETA_CAVEATS = ['Google Play sync failed 9 days ago, so the most recent data is missing', 'Sample size is small (about 640 monthly users)'];

const REWARDS_BETA: InsightSet = {
  ANALYTICS_SUMMARY: {
    headline: 'Small beta audience, with usage falling since the sync broke',
    summary:
      'About 640 people used the beta this period (down 18.2%) across 2,950 sessions. Part of the decline is an artifact: Google Play sync has been failing for nine days, so recent activity is not being counted. Reconnect the integration before drawing conclusions about beta engagement.',
    key_findings: [
      { finding: 'Active users 640, down 18.2%, but the last nine days of data are missing', metric: 'activeUsers', direction: 'down' },
      { finding: '30-day retention 34%, low but typical for an early beta', metric: 'retentionRate', direction: 'flat' },
    ],
    action_items: [
      { action: 'Reconnect the Google Play integration; the service account key has expired', priority: 'high' },
      { action: 'Recruit additional beta testers to reach a statistically useful sample', priority: 'medium' },
    ],
    data_quality: { sufficient: false, caveats: BETA_CAVEATS },
  },
  CRASH_SUMMARY: {
    headline: 'Crash-free sessions are low at 95.8% and need attention before launch',
    summary:
      'The beta is crashing noticeably more than production apps, with 95.8% crash-free sessions and a 1.4% ANR rate. Reviews point to a crash when opening the rewards catalogue on Android 13 and a splash-screen hang after the last update.',
    key_findings: [
      { finding: 'Crash-free sessions 95.8%, well below the 99% launch bar', metric: 'crashFreeSessions', direction: 'down' },
      { finding: 'Catalogue screen crash reported on Android 13', metric: 'crashes', direction: 'up' },
    ],
    action_items: [
      { action: 'Fix the Android 13 catalogue crash before expanding the beta', priority: 'high' },
      { action: 'Set a crash-free-session target of at least 99% as a launch gate', priority: 'high' },
    ],
    data_quality: { sufficient: false, caveats: BETA_CAVEATS },
  },
  PERFORMANCE_SUMMARY: {
    headline: 'Performance is below target, but stale data limits confidence',
    summary:
      'Page response time (640ms) and API response time (710ms) are above targets, and uptime is 97.4%. These figures come from the last successful sync nine days ago and may not reflect the current build.',
    key_findings: [
      { finding: 'Page response time 640ms against a 500ms target', metric: 'responseTime', direction: 'up' },
      { finding: 'Uptime 97.4% with one major incident recorded before the sync failed', metric: 'serverUptime', direction: 'down' },
    ],
    action_items: [
      { action: 'Restore the data connection, then re-check performance on the current build', priority: 'high' },
    ],
    data_quality: { sufficient: false, caveats: BETA_CAVEATS },
  },
  REVIEW_SENTIMENT: {
    headline: 'Beta testers like the concept but hit crashes and confusing redemption',
    summary:
      'The 54 beta reviews average 3.2 stars. Testers like the streak bonus idea and fresh interface. Their complaints are crashes on Android 13, battery drain in the background, and an unclear redeem flow.',
    key_findings: [
      { finding: 'Streak bonus concept and the refreshed UI are praised', metric: 'sentiment', direction: 'up' },
      { finding: 'Crashes, background battery drain and a confusing redeem flow are the main issues', metric: 'sentiment', direction: 'down' },
    ],
    action_items: [
      { action: 'Redesign the redeem screen to show which rewards the current balance can buy', priority: 'medium' },
      { action: 'Audit background wake-ups to address the battery drain reports', priority: 'medium' },
    ],
    data_quality: { sufficient: true, caveats: ['Only 54 reviews, so percentages are volatile'] },
  },
  EXECUTIVE_SUMMARY: {
    headline: 'Promising concept, not ready to launch until stability improves',
    summary:
      'The Rewards beta has positive early reception for its streak mechanic, but stability (95.8% crash-free) and a broken data sync hold it back. Reconnect the integration and fix the catalogue crash before the next beta wave.',
    key_findings: [
      { finding: 'Crash-free sessions are well below the launch bar', metric: 'crashFreeSessions', direction: 'down' },
      { finding: 'Google Play sync has been failing for nine days', metric: 'activeUsers', direction: 'n/a' },
    ],
    action_items: [
      { action: 'Reconnect Google Play and confirm data is flowing', priority: 'high' },
      { action: 'Fix the catalogue crash and re-test on Android 13', priority: 'high' },
    ],
    data_quality: { sufficient: false, caveats: BETA_CAVEATS },
  },
};

/** Fallback for apps connected during the demo: a short, honest "just getting started" read. */
const NEW_APP: InsightSet = {
  ANALYTICS_SUMMARY: {
    headline: 'Early data looks healthy and growing',
    summary:
      'This app is newly connected. Early usage shows steady growth in new users and solid session length. Insights will become sharper as more history syncs.',
    key_findings: [
      { finding: 'New users are growing week over week', metric: 'newUsers', direction: 'up' },
      { finding: 'Average session duration is stable', metric: 'sessionDuration', direction: 'flat' },
    ],
    action_items: [{ action: 'Let at least two weeks of data sync before making product decisions', priority: 'low' }],
    data_quality: { sufficient: true, caveats: ['Limited history; trends may change as more data syncs'] },
  },
  CRASH_SUMMARY: {
    headline: 'No stability concerns so far',
    summary: 'Crash-free sessions are high and no crash clusters have been detected yet.',
    key_findings: [{ finding: 'Crash-free sessions above 99%', metric: 'crashFreeSessions', direction: 'flat' }],
    action_items: [{ action: 'Enable real-time crash alerts so regressions are caught early', priority: 'low' }],
    data_quality: { sufficient: true },
  },
  PERFORMANCE_SUMMARY: {
    headline: 'Performance is within target',
    summary: 'Response times and uptime are within target thresholds.',
    key_findings: [{ finding: 'API response time within target', metric: 'apiResponseTime', direction: 'flat' }],
    action_items: [{ action: 'Set alert thresholds before the next release', priority: 'low' }],
    data_quality: { sufficient: true },
  },
  REVIEW_SENTIMENT: {
    headline: 'Not enough reviews yet for a sentiment read',
    summary: 'Only a handful of reviews have arrived so far. A sentiment summary will appear as more come in.',
    key_findings: [],
    action_items: [{ action: 'Prompt happy users to leave a review after a successful session', priority: 'low' }],
    data_quality: { sufficient: false, caveats: ['Too few reviews'] },
  },
  EXECUTIVE_SUMMARY: {
    headline: 'A promising start',
    summary: 'The app is connected and early signals are positive. Check back as more data syncs.',
    key_findings: [{ finding: 'Early usage and stability look healthy', metric: 'activeUsers', direction: 'up' }],
    action_items: [{ action: 'Complete the Firebase connection to unlock crash and performance insights', priority: 'medium' }],
    data_quality: { sufficient: true, caveats: ['Limited history'] },
  },
};

const CONTENT_BY_APP: Record<number, InsightSet> = {
  8001: SHOPPING,
  8002: LOYALTY,
  8003: DELIVERY,
  8004: REWARDS_BETA,
};

const TYPES: AiInsightType[] = ['ANALYTICS_SUMMARY', 'CRASH_SUMMARY', 'PERFORMANCE_SUMMARY', 'REVIEW_SENTIMENT', 'EXECUTIVE_SUMMARY'];

let nextInsightId = 6001;

function buildInsight(appId: number, type: AiInsightType, generatedHoursAgo = 3): AiInsight {
  const content = (CONTENT_BY_APP[appId] ?? NEW_APP)[type];
  return {
    id: nextInsightId++,
    appId,
    insightType: type,
    status: 'COMPLETED',
    model: 'demo-model',
    promptVersion: 'v1',
    inputWindowStart: hoursAgoIso(30 * 24),
    inputWindowEnd: hoursAgoIso(1),
    content: structuredClone(content),
    generatedAt: hoursAgoIso(generatedHoursAgo),
    // The beta's data is stale, so its insights are flagged as such.
    stale: appId === 8004,
    available: true,
  };
}

export const DEMO_AI_INSIGHTS: AiInsight[] = DEMO_APPS.flatMap((app) =>
  TYPES.map((type, i) => buildInsight(app.id!, type, 2 + i))
);

export function buildGeneratedInsight(appId: number, type: AiInsightType): AiInsight {
  return buildInsight(appId, type, 0);
}
